import "@shopify/polaris/build/esm/styles.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import { App } from "./App";
import { AppBridgeProvider } from "./AppBridgeProvider";
import { shouldRetryQuery } from "./api/client";
import { AuthRecoveryBoundary } from "./components/AuthRecoveryBoundary";
import { LocaleProvider } from "./components/LocaleProvider";
import { initLocale } from "./lib/i18n";

// 埋め込みアプリの慣習どおり `host` はクエリ文字列から取る。
const params = new URLSearchParams(window.location.search);
const host = params.get("host");

// 初期言語は admin の `locale`。復帰後の保存値への変更は LocaleProvider が購読する。
initLocale(window.location.search);

const apiKey = import.meta.env.VITE_SHOPIFY_API_KEY ?? "";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 本番 backend は Fly の scale-to-zero＝休止からの初回リクエストは起動に数秒〜
      // 十数秒かかる。retry 1 回では起動を跨げず初回アクセスがエラー画面で確定するため、
      // 指数バックオフで最大3回（約 1+2+4 秒）粘る。
      retry: shouldRetryQuery,
      retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 8_000),
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <LocaleProvider>
      <AppBridgeProvider apiKey={apiKey} host={host}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthRecoveryBoundary><App /></AuthRecoveryBoundary>
          </BrowserRouter>
        </QueryClientProvider>
      </AppBridgeProvider>
    </LocaleProvider>
  </StrictMode>,
);
